<template>
  <router-view />
  <ToastNotification />
  <BillingFinalizingOverlay />
  <SignInModal />
</template>

<script>
import { onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import ToastNotification from "@/components/ToastNotification.vue";
import BillingFinalizingOverlay from "@/components/BillingFinalizingOverlay.vue";
import SignInModal from "@/components/SignInModal.vue";
import { useToast } from "@/composables/useToast.js";
import { iapUtils } from "@/scripts/iapUtils.js";
import { platformBilling } from "@/scripts/platformBilling.js";
import {
  startBillingWatch,
  resetBillingConfirmation,
  hasPendingCheckout,
  armPendingCheckout,
} from "@/composables/useBillingConfirmation.js";
import {
  parseBillingLink,
  BILLING_RETURN_PATH,
  BILLING_CANCEL_PATH,
} from "@/scripts/appLinks.js";
import { usePushNotifications } from "@/composables/usePushNotifications.js";
import { setupPushRegistration } from "@/composables/usePushRegistration.js";

export default {
  name: "App",
  components: { ToastNotification, BillingFinalizingOverlay, SignInModal },
  setup() {
    const router = useRouter();
    const { t } = useI18n();
    const { showToast } = useToast();
    let lastBackPress = 0;
    let backButtonCleanup = null;
    let appUrlOpenCleanup = null;

    // Height of the viewport NOT covered by the on-screen keyboard. Consumers can
    // treat it as "space I may actually draw in".
    //
    // Platforms disagree about who accounts for the keyboard, and we cannot assume
    // either one, so measure it. Both behaviours are live on Android alone:
    //   - Android 14 (API 34) honours Capacitor's resize:'none' and never shrinks
    //     the viewport, so the occlusion is only known via the Keyboard plugin and
    //     must be subtracted here.
    //   - Android 16 (API 36) shrinks the WebView anyway. iOS and mobile browsers
    //     shrink the visual viewport too, and mobile web has no plugin at all.
    //     Subtracting there would double-count and collapse the layout.
    // So compare against the keyboard-closed baseline: if the viewport already
    // shrank, the occlusion is baked in; otherwise subtract it ourselves.
    let keyboardHeight = 0;
    // Spans willShow..didHide. The platform can resize the viewport *between*
    // those events, while keyboardHeight is still 0 — re-baselining then would
    // capture the already-shrunk height and defeat the check below.
    let keyboardActive = false;
    let baselineViewportHeight = 0;
    // Whether the platform shrinks for the keyboard is a trait of the device, not
    // of the current orientation, so latch it once observed (see latchOnKeyboardShown).
    // Rotating mid-typing leaves the baseline describing the old orientation, and
    // iOS permits landscape while Android is portrait-locked; the latch keeps that
    // stale baseline out of the decision.
    let platformShrinksForKeyboard = null;

    const readViewportHeight = () =>
      window.visualViewport ? window.visualViewport.height : window.innerHeight;

    // 8px of slack absorbs sub-pixel jitter between the two signals.
    const viewportLooksShrunk = (viewport) =>
      baselineViewportHeight > 0 && viewport < baselineViewportHeight - 8;

    const updateAppHeight = () => {
      const viewport = readViewportHeight();
      // Only a keyboard-free viewport is a valid baseline; this tracks rotation too.
      if (!keyboardActive) baselineViewportHeight = viewport;
      const alreadyShrank =
        keyboardHeight > 0 &&
        (platformShrinksForKeyboard ?? viewportLooksShrunk(viewport));
      const available = alreadyShrank
        ? viewport
        : Math.max(0, viewport - keyboardHeight);
      document.documentElement.style.setProperty(
        "--app-height",
        `${available}px`
      );
    };

    // Read the platform's behaviour only at didShow: the baseline is still the one
    // captured before willShow, and any resize has landed by now. willShow is too
    // early (nothing has resized yet) and would latch the wrong answer.
    const latchOnKeyboardShown = () => {
      if (platformShrinksForKeyboard === null && baselineViewportHeight > 0) {
        platformShrinksForKeyboard = viewportLooksShrunk(readViewportHeight());
      }
    };

    const initViewportHeight = () => {
      updateAppHeight();
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", updateAppHeight);
      } else {
        window.addEventListener("resize", updateAppHeight);
      }
    };

    let keyboardCleanup = null;

    const initKeyboard = async () => {
      if (!window.Capacitor?.isNativePlatform?.()) return;

      const { Keyboard } = await import("@capacitor/keyboard");

      const setKeyboardHeight = (height) => {
        keyboardHeight = height;
        document.documentElement.style.setProperty(
          "--keyboard-height",
          `${height}px`
        );
        updateAppHeight();
      };

      // willShow fires before the platform can resize, so it is the last moment
      // the current viewport is a trustworthy keyboard-free baseline.
      const willShowListener = await Keyboard.addListener(
        "keyboardWillShow",
        (info) => {
          keyboardActive = true;
          setKeyboardHeight(info.keyboardHeight);
        }
      );

      const showListener = await Keyboard.addListener(
        "keyboardDidShow",
        (info) => {
          // Latch first: setKeyboardHeight re-runs updateAppHeight, which should
          // already see the platform's behaviour.
          latchOnKeyboardShown();
          setKeyboardHeight(info.keyboardHeight);
        }
      );

      // Release the height as the keyboard animates away, but keep the baseline
      // locked until it is fully gone and the viewport has settled.
      const willHideListener = await Keyboard.addListener("keyboardWillHide", () =>
        setKeyboardHeight(0)
      );

      const hideListener = await Keyboard.addListener("keyboardDidHide", () => {
        keyboardActive = false;
        setKeyboardHeight(0);
      });

      keyboardCleanup = () => {
        willShowListener.remove();
        showListener.remove();
        willHideListener.remove();
        hideListener.remove();
      };
    };

    const initBackButton = async () => {
      if (!window.Capacitor?.isNativePlatform?.()) return;

      const { App: CapApp } = await import("@capacitor/app");

      const listener = await CapApp.addListener("backButton", ({ canGoBack }) => {
        // 1. Give any open modal a chance to consume the back press
        //    (Modal.vue closes itself and calls preventDefault when it does).
        const backEvent = new CustomEvent("app-back-button", { cancelable: true });
        window.dispatchEvent(backEvent);
        if (backEvent.defaultPrevented) return;

        // A static-backdrop modal is open: swallow the press so we neither
        // navigate underneath it nor exit the app.
        if (document.querySelector(".modal-mask")) return;

        // 2. If we can go back in router history
        if (canGoBack) {
          router.back();
          return;
        }

        // 3. Double-press to exit
        const now = Date.now();
        if (now - lastBackPress < 2000) {
          CapApp.exitApp();
        } else {
          lastBackPress = now;
          showToast(t("common.pressBackAgainToExit"), "info", 2000);
        }
      });

      backButtonCleanup = () => listener.remove();
    };

    const initAppUrlOpen = async () => {
      if (!window.Capacitor?.isNativePlatform?.()) return;

      const { App: CapApp } = await import("@capacitor/app");

      const { Browser } = await import("@capacitor/browser");

      const listener = await CapApp.addListener("appUrlOpen", async ({ url }) => {
        const { path: billingPath, plan } = parseBillingLink(url);
        if (!billingPath) return;

        // Set the watch phase before closing the browser: the close triggers
        // the browserFinished listener below, which resets any still-idle
        // pending checkout. Deciding first guarantees it sees "finalizing"
        // (return) or a cleared key (cancel), not a stale idle state.
        if (billingPath === BILLING_RETURN_PATH) {
          // The return link carries ?plan= (the purchased lookupKey) so the
          // watch stays plan-scoped even when iOS terminated the app during
          // external-browser checkout and the in-memory key is gone. The URL
          // value wins over a surviving key — it comes from the Stripe
          // session that actually redirected here. When absent, leave any
          // surviving key alone; with neither, startBillingWatch fails
          // closed (no watch, no overlay — the webhook still grants the
          // entitlement reactively).
          if (plan) {
            armPendingCheckout(plan);
          }
          startBillingWatch();
        } else {
          resetBillingConfirmation();
        }

        await Browser.close().catch(() => {});

        if (billingPath === BILLING_CANCEL_PATH) {
          // Same sentence as the web bounce page shows for the same event, so
          // it reuses that key rather than shipping a second translation of it
          // to all 31 bundles.
          showToast(t("billing.returnPage.cancelTitle"), "info", 2500);
        }
      });

      // Resume fallback: iOS universal links are unreliable on server
      // redirect chains and the user may return by app-switching. Android
      // custom tabs background the app, so this also covers an abandoned
      // tab there. No-op when the app link already started the watch.
      const stateListener = await CapApp.addListener(
        "appStateChange",
        ({ isActive }) => {
          if (isActive && hasPendingCheckout()) {
            startBillingWatch();
          }
        }
      );

      // Abandon cleanup — ANDROID-ONLY signal: browserFinished fires when the
      // Custom Tab closes without reaching a return/cancel page, so the
      // still-armed-and-idle checkout is cleared before the resume fallback
      // could flash a "Finalizing" overlay. iOS opens checkout in the
      // external default browser (Guideline 3.1.1 — no in-app sheet), which
      // never emits this event; the iOS abandon path is the overlay's own
      // cancel button instead. Clearing only when still armed-and-idle
      // leaves a started watch and the pay-then-app-switch path untouched.
      const browserListener = await Browser.addListener("browserFinished", () => {
        if (hasPendingCheckout()) {
          resetBillingConfirmation();
        }
      });

      appUrlOpenCleanup = () => {
        listener.remove();
        stateListener.remove();
        browserListener.remove();
      };
    };

    // Keep FCM tokens fresh across launches/rotation. Silent: never prompts
    // (only the account-page toggle triggers the permission ask).
    const {
      isSupported: isPushSupported,
      refreshTokenIfPermitted,
      listenForTokenRotation,
    } = usePushNotifications();

    onMounted(() => {
      // Finish any web signInWithRedirect that just returned. Global (rather
      // than per-page) so login from Blog/FAQ — which carry no auth listener —
      // still lands the user in the app. No-op unless a redirect is pending.
      // Dynamic import keeps App.vue out of the authUtils↔router module cycle
      // (same cycle-free pattern as router.afterEach's useTheme import).
      if (!window.Capacitor?.isNativePlatform?.()) {
        import("@/scripts/authUtils.js").then(({ authUtils }) =>
          authUtils.completePendingRedirectAuth()
        );
      }
      initViewportHeight();
      initKeyboard();
      initBackButton();
      initAppUrlOpen();
      iapUtils.initialize();
      // Resolve the iOS App Store storefront so the external-purchase-link gate
      // uses the storefront (not IP). No-op off native iOS; fails closed to
      // iap-only until it resolves.
      platformBilling.initIosStorefront().catch((e) =>
        console.error("initIosStorefront failed:", e)
      );
    });

    // mobileNotifications is a runtime flag that resolves from RTDB after
    // startup, and authStore.userId is restored asynchronously — so push
    // registration is driven reactively off both (see setupPushRegistration
    // for the cold-launch / account-switch / re-login handling). Created in
    // setup() so its watcher is disposed on unmount.
    setupPushRegistration({
      isSupported: isPushSupported,
      listenForTokenRotation,
      refreshTokenIfPermitted,
    });

    onUnmounted(() => {
      if (keyboardCleanup) keyboardCleanup();
      if (backButtonCleanup) backButtonCleanup();
      if (appUrlOpenCleanup) appUrlOpenCleanup();
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateAppHeight);
      } else {
        window.removeEventListener("resize", updateAppHeight);
      }
    });
  },
};
</script>

<style></style>
